/**
 * BentoCard — single tile in the home page bento grid.
 *
 * Variants:
 *   - "tile"    : neutral white card with a colored icon disc. Default.
 *   - "sky"     : sky→indigo gradient, used for Rapid Entry.
 *   - "emerald" : emerald→sky gradient, used for Add New Child.
 *   - "alert"   : same as "tile" but with a pulsing red badge dot,
 *                 used for Reminders.
 *
 * Encapsulates hover lift, focus ring, glow shadow, and keyboard
 * activation (Enter / Space) so callers only supply content.
 *
 *   <BentoCard
 *     variant="sky"
 *     icon={FiZap}
 *     title="Rapid Entry"
 *     description="Type Reg No → Weight → Height and save with Enter."
 *     meta="Fast"
 *     watermark={FiZap}
 *     footerLabel="Open Rapid Entry"
 *     onClick={() => navigate('/rapid-entry')}
 *   />
 */
import React from "react";
import { FiArrowUpRight, FiLock } from "react-icons/fi";
import "./BentoCard.css";

export function BentoCard({
    variant = "tile",
    icon: Icon,
    title,
    description,
    meta,
    badge,
    badgeTone = "danger",
    watermark: Watermark,
    footerLabel,
    onClick,
    onFooterClick,
    className = "",
    style,
    ariaLabel,
    loading = false,
    disabled = false,
    disabledLabel = "Coming soon",
    children,
}) {
    const interactive = typeof onClick === "function" && !disabled;
    const isGradient = variant === "sky" || variant === "emerald";
    const variantClass = `wp-bento-card--${variant}`;

    const handleKeyDown = (e) => {
        if (!interactive) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(e);
        }
    };

    const handleFooterClick = (e) => {
        e.stopPropagation();
        if (typeof onFooterClick === "function") {
            onFooterClick(e);
        } else if (interactive) {
            onClick(e);
        }
    };

    return (
        <div
            className={`wp-bento-card ${variantClass} ${interactive ? "is-interactive" : ""} ${loading ? "is-loading" : ""} ${disabled ? "is-disabled" : ""} ${className}`}
            onClick={interactive ? onClick : undefined}
            onKeyDown={interactive ? handleKeyDown : undefined}
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-label={ariaLabel || (typeof title === "string" ? title : undefined)}
            aria-busy={loading || undefined}
            aria-disabled={disabled || undefined}
            style={style}
        >
            {/* Decorative top accent line for gradient cards. */}
            {isGradient ? <span className="wp-bento-card__shine" aria-hidden="true" /> : null}

            {/* Header row: meta pill (left) + lock/chevron (right). */}
            <div className="wp-bento-card__header">
                {meta ? <span className="wp-bento-card__meta">{meta}</span> : <span />}
                {disabled ? (
                    <span className="wp-bento-card__lock" aria-hidden="true">
                        <FiLock size={14} strokeWidth={2.25} />
                    </span>
                ) : interactive ? (
                    <span className="wp-bento-card__chevron" aria-hidden="true">
                        <FiArrowUpRight size={18} strokeWidth={2.5} />
                    </span>
                ) : null}
            </div>

            {/* Icon disc with optional badge. */}
            <div className={`wp-bento-card__icon-wrap ${badge ? "has-badge" : ""}`}>
                <div className="wp-bento-card__icon">
                    {Icon ? <Icon size={26} strokeWidth={2.25} /> : null}
                    {badge != null ? (
                        <span className={`wp-bento-card__badge wp-bento-card__badge--${badgeTone}`}>
                            {badge}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Body. */}
            <div className="wp-bento-card__body">
                <h3 className="wp-bento-card__title">{title}</h3>
                {description ? (
                    <p className="wp-bento-card__desc">{description}</p>
                ) : null}
                {disabled ? (
                    <span className="wp-bento-card__disabled-pill">
                        <FiLock size={11} strokeWidth={2.5} />
                        {disabledLabel}
                    </span>
                ) : null}
                {children}
            </div>

            {/* Optional footer link. */}
            {footerLabel ? (
                <span
                    className="wp-bento-card__footer"
                    onClick={handleFooterClick}
                    role={interactive ? "link" : undefined}
                    tabIndex={interactive ? 0 : undefined}
                    onKeyDown={
                        interactive
                            ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleFooterClick(e);
                                  }
                              }
                            : undefined
                    }
                >
                    {footerLabel}
                    <span className="wp-bento-card__footer-arrow" aria-hidden="true">→</span>
                </span>
            ) : null}

            {/* Decorative watermark. */}
            {Watermark ? (
                <Watermark
                    className="wp-bento-card__watermark"
                    size={isGradient ? 180 : 130}
                    strokeWidth={1.4}
                    aria-hidden="true"
                />
            ) : null}
        </div>
    );
}

export default BentoCard;
